import React, { useState, useEffect } from 'react';
import { Plus, Search, FileText, Edit2, Trash2, Copy, Eye, Star, Filter } from 'lucide-react';
import api from '../lib/api';
import Modal from '../components/Modal';
import { Input, Select, Textarea } from '../components/Form';

interface ContractTemplateClause {
  id?: string;
  order: number;
  title: string;
  content: string;
  isRequired: boolean;
}

interface ContractTemplate {
  id: string;
  name: string;
  type: string;
  description?: string;
  content: string;
  variables?: Record<string, any>;
  isActive: boolean;
  isDefault: boolean;
  clauses: ContractTemplateClause[];
  _count?: {
    leases: number;
  };
}

export default function ContractTemplatesPage() {
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('ALL');
  const [filterActive, setFilterActive] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ContractTemplate | null>(null);
  const [previewContent, setPreviewContent] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    type: 'RENTAL',
    description: '',
    content: '',
    isActive: true,
    isDefault: false,
    clauses: [] as ContractTemplateClause[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadTemplates();
  }, [filterType, filterActive]);

  const loadTemplates = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== 'ALL') params.append('type', filterType);
      if (filterActive !== 'ALL') params.append('onlyActive', filterActive === 'ACTIVE' ? 'true' : 'false');

      const response = await api.get(`/contract-templates?${params.toString()}`);
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (template?: ContractTemplate) => {
    if (template) {
      setSelectedTemplate(template);
      setFormData({
        name: template.name,
        type: template.type,
        description: template.description || '',
        content: template.content,
        isActive: template.isActive,
        isDefault: template.isDefault,
        clauses: template.clauses || [],
      });
    } else {
      setSelectedTemplate(null);
      setFormData({
        name: '',
        type: 'RENTAL',
        description: '',
        content: '',
        isActive: true,
        isDefault: false,
        clauses: [],
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
    setErrors({});
  };

  const handleAddClause = () => {
    const newClause: ContractTemplateClause = {
      order: formData.clauses.length + 1,
      title: '',
      content: '',
      isRequired: true,
    };
    setFormData({
      ...formData,
      clauses: [...formData.clauses, newClause],
    });
  };

  const handleRemoveClause = (index: number) => {
    const updatedClauses = formData.clauses.filter((_, i) => i !== index);
    // Reordenar
    const reorderedClauses = updatedClauses.map((clause, idx) => ({
      ...clause,
      order: idx + 1,
    }));
    setFormData({ ...formData, clauses: reorderedClauses });
  };

  const handleClauseChange = (index: number, field: keyof ContractTemplateClause, value: any) => {
    const updatedClauses = [...formData.clauses];
    updatedClauses[index] = { ...updatedClauses[index], [field]: value };
    setFormData({ ...formData, clauses: updatedClauses });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.content.trim() || formData.content.length < 50) {
      newErrors.content = 'Conteúdo deve ter no mínimo 50 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        name: formData.name,
        type: formData.type,
        description: formData.description || undefined,
        content: formData.content,
        isActive: formData.isActive,
        isDefault: formData.isDefault,
        clauses: formData.clauses.length > 0 ? formData.clauses : undefined,
      };

      if (selectedTemplate) {
        await api.patch(`/contract-templates/${selectedTemplate.id}`, payload);
      } else {
        await api.post('/contract-templates', payload);
      }

      await loadTemplates();
      handleCloseModal();
    } catch (error: any) {
      console.error('Erro ao salvar template:', error);
      const errorMsg = error.response?.data?.message || 'Erro ao salvar template';
      setErrors({ submit: Array.isArray(errorMsg) ? errorMsg.join(', ') : errorMsg });
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;

    try {
      await api.delete(`/contract-templates/${selectedTemplate.id}`);
      await loadTemplates();
      setShowDeleteModal(false);
      setSelectedTemplate(null);
    } catch (error: any) {
      console.error('Erro ao excluir template:', error);
      alert(error.response?.data?.message || 'Erro ao excluir template');
    }
  };

  const handleDuplicate = async (template: ContractTemplate) => {
    const newName = prompt(`Duplicar template "${template.name}".\nDigite o nome do novo template:`, `${template.name} (Cópia)`);
    if (!newName) return;

    try {
      await api.post(`/contract-templates/${template.id}/duplicate`, { name: newName });
      await loadTemplates();
    } catch (error: any) {
      console.error('Erro ao duplicar template:', error);
      alert(error.response?.data?.message || 'Erro ao duplicar template');
    }
  };

  const handlePreview = async (template: ContractTemplate) => {
    setPreviewContent(template.content);
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch =
      t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const typeLabels: Record<string, string> = {
    RENTAL: 'Locação',
    SALE: 'Venda',
    PROPERTY_MANAGEMENT: 'Gestão de Imóvel',
    SERVICE: 'Prestação de Serviço',
    OTHER: 'Outro',
  };

  const typeColors: Record<string, string> = {
    RENTAL: 'bg-blue-100 text-blue-800',
    SALE: 'bg-green-100 text-green-800',
    PROPERTY_MANAGEMENT: 'bg-purple-100 text-purple-800',
    SERVICE: 'bg-orange-100 text-orange-800',
    OTHER: 'bg-gray-100 text-gray-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Templates de Contratos</h1>
        <button onClick={() => handleOpenModal()} className="btn-primary flex items-center space-x-2">
          <Plus className="w-5 h-5" />
          <span>Novo Template</span>
        </button>
      </div>

      <div className="card space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou descrição..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>

        <div className="flex space-x-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input"
          >
            <option value="ALL">Todos os Tipos</option>
            <option value="RENTAL">Locação</option>
            <option value="SALE">Venda</option>
            <option value="PROPERTY_MANAGEMENT">Gestão de Imóvel</option>
            <option value="SERVICE">Prestação de Serviço</option>
            <option value="OTHER">Outro</option>
          </select>

          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="input"
          >
            <option value="ALL">Todos</option>
            <option value="ACTIVE">Ativos</option>
            <option value="INACTIVE">Inativos</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="card hover:shadow-lg transition-shadow">
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-primary-600" />
                  {template.isDefault && (
                    <span title="Template Padrão">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[template.type]}`}>
                    {typeLabels[template.type]}
                  </span>
                  {!template.isActive && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Inativo
                    </span>
                  )}
                </div>
              </div>

              <h3 className="font-bold text-lg line-clamp-2">{template.name}</h3>

              {template.description && (
                <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>
              )}

              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>{template.clauses?.length || 0} cláusula(s)</span>
                {template._count && template._count.leases > 0 && (
                  <span>{template._count.leases} uso(s)</span>
                )}
              </div>

              <div className="flex space-x-2 pt-3 border-t">
                <button
                  onClick={() => handlePreview(template)}
                  className="flex-1 px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center space-x-1"
                  title="Visualizar"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDuplicate(template)}
                  className="flex-1 px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors flex items-center justify-center space-x-1"
                  title="Duplicar"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleOpenModal(template)}
                  className="flex-1 px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors flex items-center justify-center space-x-1"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowDeleteModal(true);
                  }}
                  className="flex-1 px-3 py-2 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center space-x-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhum template encontrado</p>
        </div>
      )}

      {/* Modal de Criação/Edição */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={selectedTemplate ? 'Editar Template' : 'Novo Template'}
        size="2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Nome do Template"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={errors.name}
                placeholder="Ex: Contrato de Locação Residencial"
                required
              />
            </div>

            <Select
              label="Tipo de Contrato"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              options={[
                { value: 'RENTAL', label: 'Locação' },
                { value: 'SALE', label: 'Venda' },
                { value: 'PROPERTY_MANAGEMENT', label: 'Gestão de Imóvel' },
                { value: 'SERVICE', label: 'Prestação de Serviço' },
                { value: 'OTHER', label: 'Outro' },
              ]}
              required
            />

            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm font-medium">Ativo</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm font-medium">Padrão</span>
              </label>
            </div>
          </div>

          <Textarea
            label="Descrição"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Descrição do template..."
            rows={2}
          />

          <Textarea
            label="Conteúdo do Contrato"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            error={errors.content}
            placeholder="Digite o conteúdo do contrato. Use {{variavel}} para variáveis dinâmicas..."
            rows={10}
            required
          />

          {/* Cláusulas */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Cláusulas</h3>
              <button
                type="button"
                onClick={handleAddClause}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar Cláusula</span>
              </button>
            </div>

            <div className="space-y-4">
              {formData.clauses.map((clause, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Cláusula {clause.order}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveClause(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <Input
                    label="Título"
                    value={clause.title}
                    onChange={(e) => handleClauseChange(index, 'title', e.target.value)}
                    placeholder="Ex: Do Objeto do Contrato"
                    required
                  />

                  <Textarea
                    label="Conteúdo"
                    value={clause.content}
                    onChange={(e) => handleClauseChange(index, 'content', e.target.value)}
                    placeholder="Texto da cláusula..."
                    rows={3}
                    required
                  />

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={clause.isRequired}
                      onChange={(e) => handleClauseChange(index, 'isRequired', e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Cláusula obrigatória</span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errors.submit}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleCloseModal}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              {selectedTemplate ? 'Salvar Alterações' : 'Criar Template'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de Exclusão */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirmar Exclusão"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Tem certeza que deseja excluir o template <strong>{selectedTemplate?.name}</strong>?
          </p>
          <p className="text-sm text-red-600">Esta ação não pode ser desfeita.</p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Excluir
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de Prévia */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title={`Prévia: ${selectedTemplate?.name}`}
        size="xl"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 border rounded-lg p-4">
            <h4 className="font-semibold mb-2">Conteúdo do Contrato:</h4>
            <div className="whitespace-pre-wrap text-sm text-gray-700">
              {previewContent}
            </div>
          </div>

          {selectedTemplate && selectedTemplate.clauses && selectedTemplate.clauses.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Cláusulas:</h4>
              <div className="space-y-3">
                {selectedTemplate.clauses.map((clause) => (
                  <div key={clause.id} className="bg-gray-50 border rounded-lg p-3">
                    <h5 className="font-medium text-sm mb-1">
                      {clause.order}. {clause.title}
                      {clause.isRequired && (
                        <span className="ml-2 text-xs text-red-600">(Obrigatória)</span>
                      )}
                    </h5>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{clause.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <button
              onClick={() => setShowPreviewModal(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Fechar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
